import { GetStaticPaths, GetStaticProps } from "next"
import Collections from "@views/Collections"
import {
  getAllCategoryConfigs,
  getCategoryConfig,
  getProducts,
  getNewReleases,
} from "@views/Collections/queries"
import { getClient } from "@utils/sanity"
import { formatRFC3339, subMonths } from "date-fns"
import _ from "lodash"

export default Collections

export const getStaticPaths: GetStaticPaths = async () => {
  const categoryConfigs = await getClient().fetch(getAllCategoryConfigs)

  const paths = [] as any

  categoryConfigs.forEach((topLevel) => {
    const topLevelCat = topLevel.category
    const topLevelSubs = topLevel.subcategories

    paths.push({ params: { path: [topLevelCat.slug] } })
    paths.push({ params: { path: [topLevelCat.slug, "new-releases"] } })

    topLevelSubs.forEach((topLevelSub) => {
      const subLevelCat = topLevelSub.category
      const subLevelSubs = topLevelSub.subcategories

      if (subLevelCat) {
        paths.push({ params: { path: [topLevelCat.slug, subLevelCat.slug] } })

        if (subLevelSubs) {
          subLevelSubs.forEach((subLevelSub) => {
            if (subLevelSub._type === "category") {
              const nestedLevelCat = subLevelSub.category

              paths.push({
                params: {
                  path: [
                    topLevelCat.slug,
                    subLevelCat.slug,
                    nestedLevelCat.slug,
                  ],
                },
              })
            } else if (subLevelSub._type === "categoryWithSubcategories") {
              const nestedLevelCat = subLevelSub.category
              const nestedLevelSubs = subLevelSub.subcategories

              paths.push({
                params: {
                  path: [
                    topLevelCat.slug,
                    subLevelCat.slug,
                    nestedLevelCat.slug,
                  ],
                },
              })

              nestedLevelSubs.forEach((nestedLevelSub) => {
                paths.push({
                  params: {
                    path: [
                      topLevelCat.slug,
                      subLevelCat.slug,
                      nestedLevelCat.slug,
                      nestedLevelSub.slug,
                    ],
                  },
                })
              })
            }
          })
        }
      }
    })
  })

  return {
    paths: paths || null,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps = async ({
  params = {},
  preview = false,
}) => {
  const path: Array<string> = (params.path as any) || []
  const [topLevelCategory] = path
  const useNewReleases =
    path?.length && path[path.length - 1] === "new-releases"
  const thisCategory = useNewReleases
    ? path?.length && path[path.length - 2]
    : path?.length && path[path.length - 1]

  const categoryConfig = await getClient().fetch(getCategoryConfig, {
    slug: topLevelCategory,
  })

  const categoryMap = {}

  if (categoryConfig) {
    categoryMap[categoryConfig.category.slug] = [categoryConfig.category._id]

    const topLevelSubs = categoryConfig ? categoryConfig.subcategories : []

    topLevelSubs.forEach((topLevelSub) => {
      const subLevelCat = topLevelSub.category
      const subLevelSubs = topLevelSub.subcategories

      if (subLevelCat) {
        categoryMap[categoryConfig.category.slug].push(subLevelCat._id)
        categoryMap[subLevelCat.slug] = [subLevelCat._id]

        if (subLevelSubs) {
          subLevelSubs.forEach((subLevelSub) => {
            if (subLevelSub._type === "category") {
              const nestedLevelCat = subLevelSub.category

              categoryMap[categoryConfig.category.slug].push(nestedLevelCat._id)
              categoryMap[subLevelCat.slug].push(nestedLevelCat._id)
              categoryMap[nestedLevelCat.slug] = [nestedLevelCat._id]
            } else if (subLevelSub._type === "categoryWithSubcategories") {
              const nestedLevelCat = subLevelSub.category
              const nestedLevelSubs = subLevelSub.subcategories

              categoryMap[categoryConfig.category.slug].push(nestedLevelCat._id)
              categoryMap[subLevelCat.slug].push(nestedLevelCat._id)
              categoryMap[nestedLevelCat.slug] = [nestedLevelCat._id]

              nestedLevelSubs.forEach((nestedLevelSub) => {
                categoryMap[categoryConfig.category.slug].push(
                  nestedLevelSub._id
                )
                categoryMap[subLevelCat.slug].push(nestedLevelSub._id)
                categoryMap[nestedLevelCat.slug].push(nestedLevelSub._id)
                categoryMap[nestedLevelSub.slug] = [nestedLevelSub._id]
              })
            }
          })
        }
      }
    })
  }

  const query = useNewReleases ? getNewReleases : getProducts
  const queryVariables: any = {
    categoryIds: categoryMap[thisCategory],
    pathSlugs: useNewReleases ? path.slice(0, path.length - 1) : path,
  }

  if (useNewReleases) {
    queryVariables.releaseDate = formatRFC3339(subMonths(new Date(), 6))
  }

  const pageData = categoryMap[thisCategory]
    ? await getClient(preview).fetch(query, queryVariables)
    : {}

  return {
    props: {
      preview,
      query,
      queryVariables,
      pageData,
      path: useNewReleases ? path.slice(0, path.length - 1) : path,
      categoryMap,
      categoryConfig,
      useNewReleases,
    },
  }
}
